import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@arcadeai/design-system/components/ui/atoms/accordion";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@arcadeai/design-system/components/ui/atoms/alert";
import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { cn } from "@arcadeai/design-system/lib/utils";
import {
  OAUTH_PROVIDER_CATALOGUE,
  OAUTH2_PROVIDER,
} from "@arcadeai/design-system/metadata/oauth-providers";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ExternalLink,
  Key,
  TriangleAlert,
  X,
} from "lucide-react";
import { useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import {
  type RequirementToolInput,
  useToolRequirementsStatus,
} from "@/hooks/use-tool-requirements-status";

type WarningMissingRequirementsProps = {
  tools: RequirementToolInput | RequirementToolInput[];
  className?: string;
};

export function WarningMissingRequirements({
  tools,
  className,
}: WarningMissingRequirementsProps) {
  const toolsArray = Array.isArray(tools) ? tools : [tools];
  const [isCollapsed, setIsCollapsed] = useLocalStorage(
    "warning-missing-requirements-collapsed",
    true
  );

  const { analyzeBatchRequirements } = useToolRequirementsStatus();
  const { missingSecretsMap, missingOAuthMap, hasMissingRequirements } =
    useMemo(
      () => analyzeBatchRequirements(toolsArray),
      [analyzeBatchRequirements, toolsArray]
    );

  const missingSecrets = Object.keys(missingSecretsMap);
  const missingOAuth = Object.keys(missingOAuthMap);

  if (!hasMissingRequirements) {
    return null;
  }

  if (isCollapsed) {
    return (
      <div className={cn("flex justify-start", className)}>
        <Button
          className="flex items-center gap-2 text-orange-500"
          onClick={() => setIsCollapsed(false)}
          size="sm"
          variant="secondary"
        >
          <AlertTriangle className="h-4 w-4" />
          <span className="hidden sm:inline">Show Missing Requirements</span>
        </Button>
      </div>
    );
  }

  return (
    <Alert className={className} variant="warning">
      <AlertTitle className="flex w-full items-center justify-between text-orange-500">
        <div className="flex items-center gap-2">
          <TriangleAlert className="size-4 text-orange-500" />
          Missing Requirements
        </div>
        <Button
          className="h-8 w-8 text-foreground"
          onClick={() => setIsCollapsed(true)}
          size="icon"
          variant="ghost"
        >
          <X className="h-4 w-4" />
        </Button>
      </AlertTitle>
      <AlertDescription className="w-full">
        <p className="mb-4">
          The following requirements are needed by your tools:
        </p>
        <Accordion className="w-full space-y-2" collapsible type="single">
          {missingSecrets.map((secret) => (
            <AccordionItem
              className="rounded-lg border px-4"
              key={`secret-${secret}`}
              value={`secret-${secret}`}
            >
              <AccordionTrigger className="cursor-pointer py-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-zinc-500" />
                  <span className="font-medium">{secret}</span>
                  <Badge className="ml-2" variant="destructive">
                    Missing
                  </Badge>
                  <Link
                    className="text-muted-foreground hover:text-foreground"
                    from="/orgs/$organizationId/projects/$projectId/auth/secrets"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    rel="noopener noreferrer"
                    search={{
                      type: "new",
                      newKey: secret,
                    }}
                    target="_blank"
                    to="."
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-3">
                <div className="border-orange-200 border-l-2 pl-4">
                  <p className="mb-2 text-muted-foreground text-sm">
                    Required by:
                  </p>
                  <ul className="list-disc space-y-1.5 pl-5">
                    {missingSecretsMap[secret].map((tool) => (
                      <li key={tool.fully_qualified_name}>
                        <span className="font-medium">{tool.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
          {missingOAuth.map((oauthProvider) => {
            const {
              tools: providerTools,
              displayName,
              isDisabled,
            } = missingOAuthMap[oauthProvider];
            const descriptor =
              OAUTH_PROVIDER_CATALOGUE[oauthProvider] ?? OAUTH2_PROVIDER;
            return (
              <AccordionItem
                className="rounded-lg border px-4"
                key={`oauth-${oauthProvider}`}
                value={`oauth-${oauthProvider}`}
              >
                <AccordionTrigger className="cursor-pointer py-3 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <descriptor.icon className="h-4 w-4 text-zinc-500" />
                    <span className="font-medium">{displayName}</span>
                    <Badge
                      className="ml-2"
                      variant={isDisabled ? "secondary" : "destructive"}
                    >
                      {isDisabled ? "Disabled" : "Missing"}
                    </Badge>
                    {isDisabled ? (
                      <Link
                        className="text-muted-foreground hover:text-foreground"
                        from="/orgs/$organizationId/projects/$projectId/auth/oauth"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        rel="noopener noreferrer"
                        search={{
                          name: oauthProvider,
                        }}
                        target="_blank"
                        to="."
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    ) : null}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-3">
                  <div className="border-orange-200 border-l-2 pl-4">
                    <p className="mb-2 text-muted-foreground text-sm">
                      Required by:
                    </p>
                    <ul className="list-disc space-y-1.5 pl-5">
                      {providerTools.map((tool) => (
                        <li key={tool.fully_qualified_name}>
                          <span className="font-medium">{tool.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </AlertDescription>
    </Alert>
  );
}
